import React from 'react';
import { createPortal } from 'react-dom';
import NodeFieldLabel from './NodeFieldLabel';
import {
  withDescriptionTooltip,
  type SelectFieldOption,
} from './selectFieldOption';
import { usePortaledMenu } from './usePortaledMenu';
import { useListboxKeys } from './useListboxKeys';
import { CheckMark, OptionLabel, OverflowCount } from './MultiSelectField.styled';
import {
  SelectBtn,
  SelectCaret,
  SelectMenu,
  SelectOption,
  SelectValue,
  SelectWrap,
} from './nodeCommon.styled';

/** Mirrors `ConfigFieldOptionDto.exclusive`: pickable only on its own. */
export interface MultiSelectFieldOption extends SelectFieldOption {
  readonly exclusive?: boolean;
}

export interface MultiSelectFieldProps {
  readonly label?: string;
  readonly hint?: string | null;
  readonly hintTestId?: string;
  readonly options: readonly MultiSelectFieldOption[];
  readonly value: readonly string[];
  readonly onChange: (next: string[]) => void;
  readonly readOnly?: boolean;
  readonly placeholder?: string;
  readonly ariaLabel?: string;
  /** Trigger test id; option buttons derive `${testId}__option-VALUE`. */
  readonly testId: string;
  /** Absent or null = uncapped. */
  readonly maxSelected?: number | null;
}

const isExclusive = (
  options: readonly MultiSelectFieldOption[],
  value: string,
): boolean => options.find((o) => o.value === value)?.exclusive === true;

/** On `SelectField`'s pill primitives so the two are indistinguishable on the
 *  node card. The trigger reads "CadQuery +2". */
const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  label,
  hint,
  hintTestId,
  options,
  value,
  onChange,
  readOnly = false,
  placeholder,
  ariaLabel,
  testId,
  maxSelected,
}) => {
  const { open, wrapRef, menuRef, openMenu, closeMenu } = usePortaledMenu();

  const showPlaceholder = value.length === 0 && !!placeholder;
  const leadLabel =
    options.find((o) => o.value === value[0])?.label ?? value[0] ?? '';
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  // Picking an exclusive option replaces the whole selection, so held ones
  // never count against the cap and offering one is never blocked.
  const combinable = React.useMemo(
    () => value.filter((v) => !isExclusive(options, v)),
    [options, value],
  );
  const atCap = maxSelected != null && combinable.length >= maxSelected;
  const capped = (option: MultiSelectFieldOption, checked: boolean) =>
    atCap && !checked && !option.exclusive;

  const toggle = (option: MultiSelectFieldOption) => {
    const checked = value.includes(option.value);
    if (readOnly || capped(option, checked)) return;
    if (!checked) {
      onChange(option.exclusive ? [option.value] : [...combinable, option.value]);
    } else if (value.length > 1) {
      // An empty selection would not publish, so the last one stays put.
      onChange(value.filter((v) => v !== option.value));
    }
  };

  const { activeIndex, setActiveIndex, onKeyDown } = useListboxKeys({
    itemCount: options.length,
    open,
    openMenu,
    closeMenu,
    onCommit: (index) => {
      const option = options[index];
      if (option) toggle(option);
    },
    spaceCommits: true,
  });

  const activeOption = open ? options[activeIndex] : undefined;
  const menuId = `${testId}__menu`;

  const trigger = (
    <SelectBtn
      type="button"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      aria-label={ariaLabel ?? label}
      aria-activedescendant={
        activeOption ? `${testId}__option-${activeOption.value}` : undefined
      }
      data-testid={testId}
      disabled={readOnly}
      onPointerDown={stop}
      onKeyDown={onKeyDown}
      onClick={(e) => {
        stop(e);
        if (readOnly) return;
        if (open) closeMenu();
        else openMenu();
      }}
    >
      <SelectValue $placeholder={showPlaceholder}>
        {showPlaceholder ? placeholder : leadLabel}
      </SelectValue>
      {value.length > 1 && (
        <OverflowCount data-testid={`${testId}__overflow`}>
          +{value.length - 1}
        </OverflowCount>
      )}
      <SelectCaret className="material-symbols-rounded" aria-hidden="true">
        expand_more
      </SelectCaret>
    </SelectBtn>
  );

  return (
    <div>
      {label ? (
        <NodeFieldLabel label={label} hint={hint} dataTestId={hintTestId} />
      ) : null}
      <SelectWrap ref={wrapRef}>
        {trigger}
        {open &&
          createPortal(
            <SelectMenu
              ref={menuRef}
              id={menuId}
              role="listbox"
              aria-multiselectable
              $portal
              onPointerDown={stop}
            >
              {options.map((opt, index) => {
                const checked = value.includes(opt.value);
                const row = (
                  <SelectOption
                    type="button"
                    id={`${testId}__option-${opt.value}`}
                    role="option"
                    aria-selected={checked}
                    $active={checked}
                    $highlighted={open && index === activeIndex}
                    disabled={capped(opt, checked)}
                    data-testid={`${testId}__option-${opt.value}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={(e) => {
                      stop(e);
                      toggle(opt);
                    }}
                  >
                    <OptionLabel>{opt.label}</OptionLabel>
                    {checked && (
                      <CheckMark
                        className="material-symbols-rounded"
                        aria-hidden="true"
                      >
                        check
                      </CheckMark>
                    )}
                  </SelectOption>
                );
                return (
                  <React.Fragment key={opt.value}>
                    {withDescriptionTooltip(opt, row)}
                  </React.Fragment>
                );
              })}
            </SelectMenu>,
            document.body,
          )}
      </SelectWrap>
    </div>
  );
};

export default MultiSelectField;
